import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import HomePage from './components/HomePage';
import AIImplementationPage from './components/AIImplementationPage';
import QATestingPage from './components/QATestingPage';
import CyberSecurityPage from './components/CyberSecurityPage';
import ContactPage from './components/ContactPage';

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/ai-implementation', label: 'AI Implementation', type: 'service' },
    { path: '/qa-testing', label: 'QA & Testing', type: 'service' },
    { path: '/cyber-security', label: 'Cyber Security', type: 'service' },
    { path: '/contact', label: 'Contact', type: 'contact' },
  ];

  return (
    <nav className="fixed top-0 w-full bg-background/95 backdrop-blur-sm border-b border-border z-50" role="navigation" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3">
            <img src="/logo.svg" alt="Semando Logo" className="h-8 w-8" />
            <span className="text-2xl font-bold text-foreground tracking-wider font-sans">SEMANDO</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.filter(item => item.type === 'service').map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm transition-all duration-200 hover:text-primary relative ${
                  location.pathname === item.path 
                    ? 'text-foreground after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:rounded-full' 
                    : 'text-muted-foreground'
                }`}
                aria-current={location.pathname === item.path ? 'page' : undefined}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Separator */}
            <div className="h-6 w-px bg-border" />
            
            {navItems.filter(item => item.type === 'contact').map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm transition-all duration-200 hover:text-primary relative ${
                  location.pathname === item.path 
                    ? 'text-foreground after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:rounded-full' 
                    : 'text-muted-foreground'
                }`}
                aria-current={location.pathname === item.path ? 'page' : undefined}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMenuOpen ? 'Close main menu' : 'Open main menu'}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden" id="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-background border-t border-border" role="menu">
              {navItems.filter(item => item.type === 'service').map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 text-sm transition-colors hover:text-primary ${
                    location.pathname === item.path ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Separator for mobile */}
              <div className="border-t border-border my-2" />
              
              {navItems.filter(item => item.type === 'contact').map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 text-sm transition-colors hover:text-primary ${
                    location.pathname === item.path ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default function App() {
  return (
    <Router>
      <div className="min-h-screen bg-background text-foreground dark">
        {/* Skip to main content link for accessibility */}
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 bg-primary text-primary-foreground px-4 py-2 rounded"
        >
          Skip to main content
        </a>
        <Navigation />
        <main className="pt-16" id="main-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/ai-implementation" element={<AIImplementationPage />} />
            <Route path="/qa-testing" element={<QATestingPage />} />
            <Route path="/cyber-security" element={<CyberSecurityPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}